import { TFunction } from 'i18next'
import { useCallback, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import ProductMeasuringGuide from '@src/quince-site-common/src/domains/contentful/ContentfulMeasuringGuide'
import { ContentfulSizeChart } from '@src/quince-site-common/src/domains/contentful/ContentfulProduct'
import { getProductPropertiesForTracking } from '@src/telemetry/getProductPropertiesForTracking'
import { TELEMETRY_EVENT_NAMES } from '@src/telemetry/telemetry-events.constants'
import { trackEvent } from '@src/telemetry/track'

import DownArrowIcon from '../../icons/downArrow.inline.svg'
import SizeGuideModal from '../modal'
import NavigationArrows from '../navigationArrows/NavigationArrows'
import MeasuringGuide from '../sizeChart/components/measuringGuide/MeasuringGuide'
import { SizeGuideProps } from '../sizeGuide/SizeGuide'
import Tabs, { Tab } from '../tabs'

import SizeChartHeaderV2 from './components/SizeChartHeaderV2'
import SizeChartV2, { SizeChartV2Handle, SizeChartV2ScrollState } from './components/SizeChartV2'
import styles from './sizeGuideV2.module.scss'

export const SizeGuideV2 = (props: SizeGuideProps) => {
  const [activeTab, setActiveTab] = useState(0)
  const [activeUnitTab, setActiveUnitTab] = useState(0)
  const [scrollState, setScrollState] = useState<SizeChartV2ScrollState>({
    showScrollButtons: false,
    canScrollLeft: false,
    canScrollRight: false,
  })
  const sizeChartRef = useRef<SizeChartV2Handle>(null)
  const { t } = useTranslation('common')

  const sizeChartTabs = getSizeGuideTabs({ sizeChart: props.sizeChart, measuringGuides: props.measuringGuides, t: t })
  const activeSizeGuideTab = sizeChartTabs[activeTab]
  const activeProductSizeGuides = activeSizeGuideTab ? [activeSizeGuideTab] : []
  const productProperties = props.product ? getProductPropertiesForTracking(props.product) : undefined

  const handleUnitsToggle = (index: number, selectedValue: string) => {
    setActiveUnitTab(index)
    trackEvent(TELEMETRY_EVENT_NAMES.SIZE_CHART_TOGGLE, {
      section_type: 'size_chart_toggle',
      context: {
        size_chart_name: activeSizeGuideTab?.sizeChart.title,
        size_chart_units: selectedValue,
        productProperties: productProperties,
      },
    })
  }

  const handleScrollStateChange = useCallback((state: SizeChartV2ScrollState) => {
    setScrollState(state)
  }, [])

  const prevNavClickHandler = () => {
    sizeChartRef.current?.scrollLeft()
    trackEvent(TELEMETRY_EVENT_NAMES.BUTTON_CLICK, {
      action: 'click_carousel_left',
      value: true,
      context: {
        // eslint-disable-next-line i18next/no-literal-string
        contentContainerDisplayName: 'SIZE CHART',
        productProperties: productProperties,
      },
    })
  }

  const nextNavClickHandler = () => {
    sizeChartRef.current?.scrollRight()
    trackEvent(TELEMETRY_EVENT_NAMES.BUTTON_CLICK, {
      action: 'click_carousel_right',
      value: true,
      context: {
        // eslint-disable-next-line i18next/no-literal-string
        contentContainerDisplayName: 'SIZE CHART',
        productProperties: productProperties,
      },
    })
  }

  return (
    <>
      <SizeGuideModal
        open={true}
        onClose={props.onSizeChartModalClose}
        showCloseButton={true}
        responsiveBottomSheet={true}
        className={styles.sizeChartModal}
      >
        <SizeChartHeaderV2
          headerTitle={activeSizeGuideTab?.sizeChart.title}
          onUnitsToggle={handleUnitsToggle}
          selectedSizeChartSection={activeSizeGuideTab?.sizeChart.sections?.[activeUnitTab]}
          sizeChartSections={activeSizeGuideTab?.sizeChart.sections ?? []}
          activeUnitIndex={activeUnitTab}
        />
        <div className={styles.tabsRow}>
          <Tabs
            activeTabId={activeSizeGuideTab?.tabId}
            onChange={activeId => {
              const nextTabIndex = sizeChartTabs.findIndex(tab => tab.tabId === activeId)
              if (nextTabIndex >= 0) {
                setActiveTab(nextTabIndex)
                trackEvent(TELEMETRY_EVENT_NAMES.SIZE_CHART_TAB, {
                  section_type: 'size_chart_tab',
                  context: {
                    size_chart_name: sizeChartTabs[nextTabIndex]?.sizeChart.title,
                    size_chart_tab_value: sizeChartTabs[nextTabIndex]?.tabTitle,
                    productProperties: productProperties,
                  },
                })
              }
            }}
            tabButtonWrapperClassname={styles.tabsContainer__tabButtonWrapper}
            activeTabHeaderClassName={styles.tabsContainer__tabHeader__active}
          >
            {sizeChartTabs.map(tab => (
              <Tab
                key={tab.tabId}
                id={tab.tabId}
                title={tab.tabTitle}
                headerClassName={styles.tabsContainer__tabHeader}
              >
                <SizeChartV2
                  ref={sizeChartRef}
                  activeSizeGuideUnitIndex={activeUnitTab}
                  productSizeGuides={activeProductSizeGuides}
                  onScrollStateChange={handleScrollStateChange}
                />
                {activeSizeGuideTab?.measuringGuide && (
                  <div className={styles.measuringGuide}>
                    <div className={styles.measuringGuide_title}>{t('measuring_guide')}</div>
                    <MeasuringGuide measuringGuide={activeSizeGuideTab.measuringGuide} />
                  </div>
                )}
              </Tab>
            ))}
          </Tabs>
          {scrollState.showScrollButtons && (
            <NavigationArrows
              onPrevClick={prevNavClickHandler}
              onNextClick={nextNavClickHandler}
              prevButtonDisabled={!scrollState.canScrollLeft}
              nextButtonDisabled={!scrollState.canScrollRight}
              hideArrowOnMobile={true}
              className={styles.scrollArrows}
              icon={DownArrowIcon}
              prevClassName={styles.prevIcon}
              nextClassName={styles.nextIcon}
            />
          )}
        </div>
        <div className={styles.sizeChartContent}></div>
      </SizeGuideModal>
    </>
  )
}

const getSizeGuideTabs = ({
  sizeChart,
  measuringGuides,
  t,
}: {
  sizeChart: ContentfulSizeChart
  measuringGuides?: ProductMeasuringGuide[]
  t: TFunction<'common'>
}) => {
  if (!sizeChart) return []

  // Future requirement: Ring size guide. If measuring guides > 1, create tabs from measuring guide title.
  const tabs = [
    {
      tabTitle: t('size_chart'),
      // eslint-disable-next-line i18next/no-literal-string
      tabId: 'size-chart',
      sizeChart: sizeChart,
      measuringGuide: measuringGuides?.[0],
    },
  ]

  return tabs
}
